// Moments routes are split between EC2 and Lambda after Group B migration:
//   EC2  (coreApiClient → /api/core): getMoments, getPhotos, invalidateCache
//   Lambda (apiClient  → /api):       updateVisibility, getPresignedUrls,
//                                     likes/bookmarks/comments (Feeds view)
// Direct photo URL strings use BACKEND_URL (Lambda origin) for the migrated
// photo-thumbnail / photo-preview / photo-original routes.
import { coreApiClient, apiClient, BACKEND_URL } from '../../services/api';

function token() {
  return encodeURIComponent(
    localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token') || ''
  );
}

function buildFilterParams({
  lens = 'all', circleIds = [], includeHidden = false, q = '', searchMode = '',
  sort = 'recent', bookmarkedOnly = false, eventIds = [],
} = {}) {
  const params = new URLSearchParams();
  if (lens && lens !== 'all') params.set('lens', lens);
  if (circleIds.length) params.set('circle_ids', circleIds.join(','));
  if (includeHidden) params.set('include_hidden', '1');
  if (q && q.trim()) params.set('q', q.trim());
  if (q && q.trim() && searchMode) params.set('search_mode', searchMode);
  if (sort && sort !== 'recent') params.set('sort', sort);
  if (bookmarkedOnly) params.set('bookmarked_only', '1');
  if (lens === 'specific' && eventIds.length) params.set('event_ids', eventIds.join(','));
  return params.toString();
}

const momentsApi = {
  // ── EC2 routes (/api/core) ────────────────────────────────────────────────────
  getMoments: (filters = {}) => {
    const query = buildFilterParams(filters);
    return coreApiClient.get(`/moments${query ? `?${query}` : ''}`).then((r) => r.data);
  },

  getPhotos: (filters = {}) => {
    const query = buildFilterParams(filters);
    return coreApiClient.get(`/moments/photos${query ? `?${query}` : ''}`).then((r) => r.data);
  },

  invalidateCache: () =>
    coreApiClient.post('/moments/invalidate-cache').then((r) => r.data),

  // ── Lambda routes (/api) ──────────────────────────────────────────────────────
  updateVisibility: (eventId, data) =>
    apiClient.patch(`/moments/${eventId}/visibility`, data).then((r) => r.data),

  getPresignedUrls: (photos, mediaTypes = ['thumbnails', 'previews']) =>
    apiClient.post('/moments/photo-presigned-urls', { photos, media_types: mediaTypes }).then((r) => r.data),

  // On-demand existence check for the "Download uncompressed original"
  // affordance — Moments search results don't carry uc_org_url (would mean a
  // metadata.json lookup per photo across many events), so this is fetched
  // lazily, once a photo is actually opened.
  getUcOriginalStatus: (eventId, photoId) =>
    apiClient.get(`/moments/events/${eventId}/photo-uc-status?photo_id=${encodeURIComponent(photoId)}`).then((r) => r.data),

  // Photo URL strings — now served by Lambda at /api/moments/* (changed from /api/core/moments/*)
  thumbUrl: (eventId, photoId) =>
    `${BACKEND_URL}/api/moments/events/${eventId}/photo-thumbnail?photo_id=${encodeURIComponent(photoId)}&token=${token()}`,

  previewUrl: (eventId, photoId) =>
    `${BACKEND_URL}/api/moments/events/${eventId}/photo-preview?photo_id=${encodeURIComponent(photoId)}&token=${token()}`,

  originalUrl: (eventId, photoId) =>
    `${BACKEND_URL}/api/moments/events/${eventId}/photo-original?photo_id=${encodeURIComponent(photoId)}&token=${token()}`,

  // "Uncompressed original" - resolves uc_org_url server-side and hands back
  // a presigned S3 URL for it, same broker as EventGallery's
  // eventApi.requestUcOriginalDownload (valid even before a Drive-backed
  // original has finished relaying into the cache - 404s until it lands,
  // 200 once cached; see backend/routes/uc_originals.py). Only ever resolves
  // when a genuinely distinct, higher-quality original exists; the photo's
  // uc_org_url field (checked via getUcOriginalStatus here, since search
  // results don't carry it) tells the UI whether to enable this affordance.
  // Served by the EC2/core backend so it can submit the relay job and
  // respond immediately rather than blocking on a Drive transfer. Callers
  // poll the returned URL directly against S3 - see utils/ucOriginalDownload.
  requestUcOriginalDownload: (eventId, photoId) =>
    coreApiClient.get(`/moments/events/${eventId}/photo-uc-original`, { params: { photo_id: photoId } }).then(r => r.data),

  // ── Feeds view: likes / bookmarks / comments (Lambda, /api) ──────────────────
  toggleLike: (eventId, photoId) =>
    apiClient.post(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/like`).then((r) => r.data),

  getLikers: (eventId, photoId, { limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (offset) params.set('offset', offset);
    const query = params.toString();
    return apiClient
      .get(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/likers${query ? `?${query}` : ''}`)
      .then((r) => r.data);
  },

  toggleBookmark: (eventId, photoId) =>
    apiClient.post(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/bookmark`).then((r) => r.data),

  getComments: (eventId, photoId, { limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (offset) params.set('offset', offset);
    const query = params.toString();
    return apiClient
      .get(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/comments${query ? `?${query}` : ''}`)
      .then((r) => r.data);
  },

  postComment: (eventId, photoId, { commentText, visibilityScope = 'public', visibilityCircleId } = {}) =>
    apiClient
      .post(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/comments`, {
        comment_text: commentText,
        visibility_scope: visibilityScope,
        visibility_circle_id: visibilityCircleId,
      })
      .then((r) => r.data),

  deleteComment: (eventId, photoId, commentId) =>
    apiClient
      .delete(`/moments/${eventId}/photos/${encodeURIComponent(photoId)}/comments/${commentId}`)
      .then((r) => r.data),

  // Batch like/comment counts + my like/bookmark state — fired asynchronously
  // after the photo list itself loads, same pattern as getPresignedUrls above.
  getInteractionCounts: (photos) =>
    apiClient.post('/moments/photo-interaction-counts', { photos }).then((r) => r.data),
};

export default momentsApi;
